import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Utensils, Truck, Compass, HeartPulse, Gem } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const ITEMS = [
  { icon: Truck, title: "Private Transport", desc: "Comfortable Hyundai Staria 2026 van with professional driver for every journey." },
  { icon: Compass, title: "Expert Local Guides", desc: "Bilingual specialists with deep cultural knowledge and local connections." },
  { icon: Utensils, title: "Gourmet Dining", desc: "Curated culinary experiences from fine dining to hidden local gems." },
  { icon: HeartPulse, title: "Wellness & Comfort", desc: "Handpicked boutique accommodations and wellness experiences." },
  { icon: Gem, title: "Exclusive Access", desc: "Private entrances, VIP experiences, and locations off the tourist trail." },
];

const ALL_INCLUSIVE_IMG = "https://images.pexels.com/photos/6127333/pexels-photo-6127333.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function AllInclusive() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="all-inclusive" data-testid="all-inclusive" className="py-24 md:py-32 lg:py-40 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          {/* Left: Image */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="relative"
          >
            <div className="aspect-[4/5] overflow-hidden">
              <img src={ALL_INCLUSIVE_IMG} alt="El Salvador landscape" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -right-6 w-32 h-32 border border-[#C5A059]/20" />
          </motion.div>

          {/* Right: Content */}
          <div>
            <motion.div initial="hidden" animate={inView ? "visible" : "hidden"}
              variants={{ visible: { transition: { staggerChildren: 0.1 } } }}>
              <motion.p variants={fadeUp} className="font-sans text-[#C5A059] text-[10px] md:text-xs tracking-[0.25em] uppercase mb-4">
                Beyond the Ordinary
              </motion.p>
              <motion.h2 variants={fadeUp} className="font-serif text-white text-4xl md:text-5xl font-light tracking-tight mb-6">
                What All-Inclusive
                <br />
                <span className="italic">Really Means</span>
              </motion.h2>
              <motion.p variants={fadeUp} className="font-sans text-white/60 text-sm md:text-base font-light leading-relaxed mb-12">
                Every Obsidian experience is crafted without compromise. From the moment we greet you
                to the final farewell, every detail is curated for effortless luxury.
              </motion.p>
            </motion.div>

            <div className="space-y-6">
              {ITEMS.map((item, i) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.3 + i * 0.1, duration: 0.6 }}
                    className="flex items-start gap-5 group"
                    data-testid={`all-inclusive-item-${i}`}
                  >
                    <div className="w-10 h-10 border border-white/10 flex items-center justify-center shrink-0 group-hover:border-[#C5A059]/40 transition-colors duration-500">
                      <Icon size={16} className="text-[#C5A059]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h4 className="font-sans text-white text-sm font-medium mb-1">{item.title}</h4>
                      <p className="font-sans text-white/50 text-xs leading-relaxed">{item.desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
